import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const StarRow = ({ value = 5, size = "w-3.5 h-3.5" }) => {
  const full = Math.round(value);
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`${size} ${i < full ? "fill-[#00E5FF] text-[#00E5FF]" : "text-zinc-600"}`}
        />
      ))}
    </div>
  );
};

const GoogleGlyph = () => (
  <span className="font-display font-bold text-lg tracking-tight" aria-label="Google">
    <span className="text-[#4285F4]">G</span>
    <span className="text-[#EA4335]">o</span>
    <span className="text-[#FBBC05]">o</span>
    <span className="text-[#4285F4]">g</span>
    <span className="text-[#34A853]">l</span>
    <span className="text-[#EA4335]">e</span>
  </span>
);

export const GoogleReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/api/reviews`)
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch(() => {
        if (active) setData({ enabled: false, reviews: [] });
      });
    return () => {
      active = false;
    };
  }, []);

  // Hide the whole section until real reviews exist.
  if (!data || !data.enabled || !Array.isArray(data.reviews) || data.reviews.length === 0) {
    return null;
  }

  const { rating, user_rating_count, google_maps_uri, reviews } = data;

  return (
    <section
      data-testid="google-reviews-section"
      id="reviews"
      className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-3xl mb-14 sm:mb-16">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">
            <GoogleGlyph /> <span className="text-zinc-500">Reviews</span>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
            Results worth <span className="text-gradient-blue">talking about.</span>
          </h2>

          {typeof rating === "number" && (
            <div
              className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2"
              data-testid="google-reviews-summary"
            >
              <span className="font-display text-3xl font-extrabold text-white">
                {rating.toFixed(1)}
              </span>
              <StarRow value={rating} size="w-5 h-5" />
              {typeof user_rating_count === "number" && (
                <span className="text-sm text-zinc-400">
                  {user_rating_count} Google reviews
                </span>
              )}
              {google_maps_uri && (
                <a
                  href={google_maps_uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-reviews-readall"
                  className="text-sm font-medium text-[#00E5FF] hover:text-white transition-colors underline underline-offset-4"
                >
                  Read all on Google
                </a>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id || i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              data-testid={`google-review-card-${i}`}
              className="rounded-2xl p-7 glass glass-hover flex flex-col"
            >
              <div className="flex items-center justify-between">
                <StarRow value={r.rating || 5} />
                <GoogleGlyph />
              </div>
              <p className="mt-5 text-zinc-200 leading-relaxed text-base line-clamp-6">
                {r.text || "Rated on Google."}
              </p>
              <div className="mt-auto pt-6 flex items-center gap-3">
                {r.author_photo_uri ? (
                  <img
                    src={r.author_photo_uri}
                    alt={r.author_name}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-9 h-9 rounded-full object-cover border border-white/10"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0055FF] to-[#00E5FF] flex items-center justify-center text-black font-bold text-sm">
                    {(r.author_name || "G").slice(0, 1)}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-display font-semibold text-white text-sm truncate">
                    {r.author_name}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {r.relative_publish_time}
                    {r.google_maps_uri && (
                      <>
                        {" · "}
                        <a
                          href={r.google_maps_uri}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-zinc-400 hover:text-[#00E5FF] transition-colors"
                        >
                          View
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-xs text-zinc-600">
          Live from Google, sorted by relevance. Reviews and author details are shown with Google attribution.
        </p>
      </div>
    </section>
  );
};

export default GoogleReviews;
